import type { Middleware, UnknownAction } from '@reduxjs/toolkit';
import { TagTypes } from '../../Api/Types/tagTypes';
import { bankDocumentsApi } from '../../Features/BankDocuments';
import { expenseInvoicesApi } from '../../Features/ExpenseInvoices/Service/expenseInvoicesApi';
import { invoiceImportApi } from '../../Features/InvoiceImport/Api/invoiceImportApi';
import { extendedInvoicesApi } from '../../Features/Invoices/Service/extendedInvoicesApi';
import { reservationsApi } from '../../Features/Reservation';
import { markCrossFeatureCacheStale } from './crossFeatureCacheBridgeActions';

const getInvoicePaymentReservationId = (action: UnknownAction): number | null => {
  if (
    extendedInvoicesApi.endpoints.recordPayment.matchFulfilled(action) ||
    extendedInvoicesApi.endpoints.recordRefund.matchFulfilled(action)
  ) {
    return action.payload.reservationId ?? null;
  }

  return null;
};

const getBankDocumentEntryInvoiceId = (action: UnknownAction): number | null => {
  if (bankDocumentsApi.endpoints.postBankDocumentEntry.matchFulfilled(action)) {
    return action.payload.invoiceId ?? null;
  }

  return null;
};

export const crossFeatureCacheBridgeMiddleware: Middleware =
  (storeApi) => (next) => (action) => {
    const result = next(action);

    if (
      markCrossFeatureCacheStale.match(action) &&
      action.payload.domain === 'expenseInvoices' &&
      action.payload.scope === 'list'
    ) {
      storeApi.dispatch(
        expenseInvoicesApi.util.invalidateTags([
          { type: TagTypes.ExpenseInvoice, id: TagTypes.List },
        ])
      );
    }

    // approveImport.matchFulfilled only ever matches a successful settlement — a rejected
    // approve never reaches this branch (S5c Binding scope: "rejected approve nie invaliduje").
    if (invoiceImportApi.endpoints.approveImport.matchFulfilled(action)) {
      storeApi.dispatch(
        expenseInvoicesApi.util.invalidateTags([{ type: TagTypes.ExpenseInvoice, id: TagTypes.List }])
      );
    }

    const reservationId = getInvoicePaymentReservationId(action as UnknownAction);

    if (reservationId != null) {
      storeApi.dispatch(
        reservationsApi.util.invalidateTags([
          { type: TagTypes.ReservationPayment, id: `LIST_${reservationId}` },
        ])
      );
    }

    const invoiceId = getBankDocumentEntryInvoiceId(action as UnknownAction);

    if (invoiceId != null) {
      storeApi.dispatch(
        extendedInvoicesApi.util.invalidateTags([
          { type: TagTypes.Invoice, id: TagTypes.List },
        ])
      );
    }

    return result;
  };

